import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useTheme
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

/**
 * Sidebar Navigation Component
 * Provides navigation between Filter Tool and Research Agent modes
 * Includes submenu items for Research Agent
 */
const SidebarNavigation = ({ activeMode, onModeChange, activeSubMode, onSubModeChange }) => {
  // Initialize dependencies
  const theme = useTheme();
  const drawerWidth = 240;
  
  // Track if research submenu is expanded
  // Initialize to false so first click will expand it
  const [researchExpanded, setResearchExpanded] = useState(false);
  
  // Auto-expand when activeMode is 'research'
  useEffect(() => {
    if (activeMode === 'research' && !researchExpanded) {
      setResearchExpanded(true);
    }
  }, [activeMode, researchExpanded]);  // Safe to exclude onModeChange and onSubModeChange as they're only used in event handlers

  // Define navigation items using useMemo to prevent recreation on each render
  const navItems = useMemo(() => [
    {
      id: 'filter',
      label: 'Filter Tool',
      icon: <FilterListIcon />
    },
    {
      id: 'research',
      label: 'Research Agent',
      icon: <SearchIcon />,
      subItems: [
        {
          id: 'research-new',
          label: 'New Project',
          icon: <AddIcon fontSize="small" />
        },
        {
          id: 'research-history',
          label: 'Activity History',
          icon: <HistoryIcon fontSize="small" />
        }
      ]
    }
  ], []);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { 
          width: drawerWidth, 
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.default,
          borderRight: `1px solid ${theme.palette.divider}`
        },
      }}
    >
      <Box sx={{ 
        height: '64px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        borderBottom: 'none'
      }}>
        <Typography variant="h5" component="h1" sx={{ 
          fontWeight: 700,
          color: 'text.primary',
          display: 'flex',
          alignItems: 'baseline'
        }}>
          wyspur<Typography component="span" color="primary" sx={{ fontWeight: 700 }}>AI</Typography>
        </Typography>
      </Box>
      {/* Removed horizontal divider */}
      <List>
        {/* Navigation items list */}
        {navItems && Array.isArray(navItems) ? navItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem disablePadding>
              <ListItemButton
                selected={activeMode === item.id && (!item.subItems || !activeSubMode)}
                onClick={() => {
                  onModeChange(item.id);
                  if (item.subItems) {
                    // Always expand if it's a submenu parent item
                    setResearchExpanded(true);
                    
                    // Always select the first subitem when clicking Research Agent
                    if (item.id === 'research' && onSubModeChange) {
                      onSubModeChange(item.subItems[0].id);
                    }
                  } else if (onSubModeChange) {
                    // Clear sub-mode when switching to a mode without subitems
                    onSubModeChange(null);
                  }
                }}
                sx={{
                  py: 2,
                  '&.Mui-selected': {
                    backgroundColor: `${theme.palette.primary.main}20`,
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      backgroundColor: `${theme.palette.primary.main}30`,
                    }
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  color: activeMode === item.id ? theme.palette.primary.main : 'inherit'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  primaryTypographyProps={{
                    fontWeight: activeMode === item.id ? 600 : 400,
                    color: activeMode === item.id ? theme.palette.primary.main : 'inherit',
                    fontSize: '0.9rem'
                  }}
                />
                {item.subItems && (researchExpanded ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>
            
            {/* Render sub-items if this item has them */}
            {item.subItems && (
              <Collapse in={researchExpanded && activeMode === item.id} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item && item.subItems && Array.isArray(item.subItems) && item.subItems.map((subItem) => (
                    <ListItem key={subItem.id} disablePadding>
                      <ListItemButton
                        selected={activeSubMode === subItem.id}
                        onClick={() => onSubModeChange && onSubModeChange(subItem.id)}
                        sx={{
                          py: 1.5,
                          pl: 4,
                          '&.Mui-selected': {
                            backgroundColor: `${theme.palette.primary.main}15`,
                            '&:hover': {
                              backgroundColor: `${theme.palette.primary.main}25`,
                            }
                          },
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          }
                        }}
                      >
                        <ListItemIcon sx={{
                          color: activeSubMode === subItem.id ? theme.palette.primary.main : 'inherit',
                          minWidth: '36px'
                        }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.label}
                          primaryTypographyProps={{
                            fontWeight: activeSubMode === subItem.id ? 500 : 400,
                            color: activeSubMode === subItem.id ? theme.palette.primary.main : 'inherit',
                            fontSize: '0.85rem'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        )) : <div>No navigation items available</div>}
      </List>
    </Drawer>
  );
};

export default SidebarNavigation;
